import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon, { iconStyles } from 'assets/components/icon/Icon';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { combineStrings } from 'assets/utils/data/String';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import ClaimJobStatusStateMachine from 'module/claims/components/claimJobStateMachine/ClaimJob.StatusStateMachine';
import { useClaimEdit } from '../ClaimEdit.Hooks';
import ClaimEditJobClone from './claimJobEdit/ClaimEdit.Job.Clone';
import ClaimEditJobComments from './claimJobEdit/ClaimEdit.Job.Comments';
import ClaimEditJobDelete from './claimJobEdit/ClaimEdit.Job.Delete';
import ClaimEditJobDocuments from './claimJobEdit/ClaimEdit.Job.Documents';
import ClaimEditJobEmailTemplate from './claimJobEdit/ClaimEdit.Job.EmailTemplate';
import { ClaimJobEditProvider } from './claimJobEdit/ClaimEdit.Job.Hooks';
import ClaimEditJobInfo from './claimJobEdit/ClaimEdit.Job.Info';
import ClaimEditJobItems from './claimJobEdit/claimJobEditItem/ClaimEdit.Job.Items';
import ClaimEditJobForm from './claimJobEdit/claimJobForm/ClaimEdit.Job.Form';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export default function ClaimEditJobs() {
  const { lang } = getLocales();
  const { claim, product } = useClaimEdit();

  return (
    <>
      <h2 id="jobs">{lang.jobs}</h2>
      {!claim?.claimJobs?.length && <ErrorWrapper id="jobs" children={lang.noJobs} />}
      {!!claim?.claimJobs?.length && (
        <DataMap
          data={claim?.claimJobs}
          render={({ data: job }) => {
            return (
              <ClaimJobEditProvider
                key={job.id}
                claimJobId={job.id}
                render={({
                  claimTypeReport,
                  setAction,
                  claimJob,
                  action,
                  permissions,
                  isClaimAdmin,
                  isClaimJobClonable,
                  isClaimJobEditableByStatus,
                  isPaymentAlreadyCreated,
                  canViewClaimJobComments,
                  canViewClaimJobItems,
                  canViewClaimJobDocuments,
                  isStatusChanged,
                  statusInfo,
                  changeJobStatusForm,
                  emailTemplates,
                  currentStatus,
                  setEmailAction,
                  emailAction,
                  isClaimOwner,
                  resetStatus,
                }) => {
                  return (
                    claimJob && (
                      <>
                        {changeJobStatusForm.data?.claimJobEmailTemplate && emailAction === 'changeStatusSendEmail' && (
                          <ClaimEditJobEmailTemplate />
                        )}
                        <div id={`${lang.job}-${claimJob.id}`}>
                          <Box class={ThemeWrapperStack}>
                            <h2 style={{ marginRight: 'auto' }}>
                              {combineStrings('-', product?.category?.code, claim?.id?.toString(), `J${job.jobNumber}`)}
                            </h2>
                            {claimJob.areAutomationRulesApplied && (
                              <Box
                                class={ThemeTagRounded}
                                style={{
                                  color: Theme.Color.success,
                                  backgroundColor: Theme.Color.successLight,
                                  fontSize: Theme.FontSize.S,
                                }}
                              >
                                {lang.automation.toUpperCase()}
                              </Box>
                            )}
                          </Box>
                          {/*  TODO: Add when needed! {!claimTypeReport && <ClaimEditJobAggregateInfo />} */}
                          <ClaimEditJobInfo />
                          {!(isClaimAdmin || (isClaimOwner && permissions?.EDIT_CLAIMJOB)) ||
                            (!isClaimJobEditableByStatus && action === 'edit' && <ClaimEditJobForm />)}
                          {!(isClaimAdmin || (isClaimOwner && permissions?.DELETE_CLAIMJOB)) ||
                            (!isClaimJobEditableByStatus && action === 'delete' && <ClaimEditJobDelete />)}
                          {!(isClaimAdmin || (isClaimOwner && permissions?.CREATE_CLAIMJOB)) ||
                            (!isClaimJobClonable && action === 'clone' && <ClaimEditJobClone />)}
                          <br />
                          <Box class={ThemeWrapperStack}>
                            {action === 'compressed' ? (
                              <Button
                                onClick={setAction}
                                data="expanded"
                                media={<Icon class="fas-chevron-down" />}
                                render={lang.viewMore}
                                class={ThemeButtonCircle}
                                htmlElementProps={{ title: lang.viewMore }}
                              />
                            ) : (
                              <Button
                                onClick={setAction}
                                data="compressed"
                                media={<Icon class="fas-chevron-up" />}
                                render={lang.viewLess}
                                class={ThemeButtonCircle}
                                htmlElementProps={{ title: lang.viewLess }}
                              />
                            )}
                            {permissions?.UPDATE_CLAIMJOB_STATUS_UNRESTRICTED && (
                              <Button
                                onClick={resetStatus}
                                media={<Icon class={iconStyles.common.restore} />}
                                render={lang.resetStatus}
                                class={ThemeButtonCircle}
                                disabled={
                                  claimJob.statusCode === ClaimJobStatusCode.OPEN_ACTIVE_IN_PROGRESS ||
                                  isPaymentAlreadyCreated
                                }
                                htmlElementProps={{ title: lang.reset }}
                              />
                            )}
                            <Button
                              onClick={setAction}
                              data="edit"
                              media={<Icon class="fas-edit" />}
                              render={lang.edit}
                              class={ThemeButtonCircle}
                              disabled={
                                isClaimJobEditableByStatus ||
                                !(isClaimAdmin || (isClaimOwner && permissions?.EDIT_CLAIMJOB))
                              }
                              htmlElementProps={{ title: lang.edit }}
                            />
                            <ClaimJobStatusStateMachine
                              emailTemplates={emailTemplates}
                              claimJob={claimJob}
                              statusInfo={statusInfo}
                              disabled={!(isClaimAdmin || (isClaimOwner && permissions?.UPDATE_CLAIMJOB_STATUS))}
                              isStatusChanged={isStatusChanged}
                              ownerId={changeJobStatusForm.data?.ownerId}
                              currentStatus={currentStatus}
                              changeStatus={changeJobStatusForm}
                              setEmailAction={setEmailAction}
                            />
                            <Button
                              onClick={setAction}
                              data="delete"
                              media={<Icon class="fas-trash" />}
                              render={lang.delete}
                              class={ThemeButtonCircle}
                              disabled={
                                isClaimJobEditableByStatus ||
                                !(isClaimAdmin || (isClaimOwner && permissions?.DELETE_CLAIMJOB))
                              }
                              htmlElementProps={{ title: lang.delete }}
                            />
                            <Button
                              onClick={setAction}
                              data="clone"
                              render={lang.clone}
                              disabled={
                                isClaimJobClonable || !(isClaimAdmin || (isClaimOwner && permissions?.CREATE_CLAIMJOB))
                              }
                              class={ThemeButtonCircle}
                              media={<Icon class="fas-copy" />}
                              htmlElementProps={{ title: lang.clone + ' ' + lang.claimJob }}
                            />
                          </Box>
                        </div>
                        {action !== 'compressed' && (
                          <>
                            {canViewClaimJobDocuments && <ClaimEditJobDocuments />}
                            {canViewClaimJobItems && !claimTypeReport && <ClaimEditJobItems />}
                            {canViewClaimJobComments && <ClaimEditJobComments />}
                          </>
                        )}
                      </>
                    )
                  );
                }}
              />
            );
          }}
        />
      )}
    </>
  );
}
